"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { BadgeMarquee } from "./badge-marquee";
import { G2Featured } from "./g2-featured";

export function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 grid-bg" aria-hidden />
      <div
        className="absolute left-1/2 top-0 h-[600px] w-[900px] -translate-x-1/2 rounded-full opacity-30 blur-3xl"
        style={{
          background:
            "radial-gradient(circle, rgba(139,92,246,0.5) 0%, rgba(6,182,212,0.2) 40%, transparent 70%)",
        }}
        aria-hidden
      />

      <div className="relative mx-auto max-w-7xl px-6 pb-20 pt-24 lg:pt-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mx-auto max-w-4xl"
        >
          <div className="mb-8 flex flex-col items-center gap-2">
            <Link
              href="#g2"
              className="group flex items-center gap-2 rounded-full border border-accent/40 bg-accent-soft px-4 py-1.5 text-xs font-medium text-foreground backdrop-blur transition-colors hover:border-accent"
            >
              <span className="text-accent">🏆</span>
              <span>
                <span className="text-gradient-accent font-semibold">#1 of 25</span> — G2 Results Index · Cloud Management Platforms · Spring 2026
              </span>
              <ArrowRight
                size={12}
                className="transition-transform group-hover:translate-x-0.5"
              />
            </Link>
            <Link
              href="#mcp"
              className="group flex items-center gap-2 rounded-full border border-highlight/30 bg-highlight-soft px-4 py-1.5 text-xs text-muted backdrop-blur transition-colors hover:border-highlight/60 hover:text-foreground"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-highlight opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-highlight" />
              </span>
              <span>
                New · MCP server in production — Kai lives in your AI tools
              </span>
              <ArrowRight
                size={12}
                className="transition-transform group-hover:translate-x-0.5"
              />
            </Link>
          </div>

          <h1 className="text-center text-5xl font-medium leading-[1.03] tracking-tight text-foreground sm:text-6xl lg:text-7xl">
            <span className="text-gradient-accent">Uniskai</span> — the
            <br />
            FinOps platform
            <br />
            your team actually needs.
          </h1>

          <p className="mx-auto mt-8 max-w-2xl text-center text-lg leading-relaxed text-muted sm:text-xl">
            Automated rightsizing, spot orchestration, waste cleanup, and
            commitment management across AWS, Azure, and GCP.
            <br />
            Now with{" "}
            <Link
              href="#kai"
              className="text-foreground underline decoration-accent/50 underline-offset-4 transition-colors hover:decoration-accent"
            >
              Kai, our AI FinOps Architect
            </Link>{" "}
            — generally available.
          </p>

          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              href="#demo"
              className="group inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3 text-sm font-medium text-foreground transition-all hover:bg-accent-hover hover:shadow-lg hover:shadow-accent/40"
            >
              Talk to Kai
              <ArrowRight
                size={16}
                className="transition-transform group-hover:translate-x-0.5"
              />
            </Link>
            <Link
              href="#savings"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-surface/60 px-6 py-3 text-sm font-medium text-foreground backdrop-blur transition-all hover:border-muted hover:bg-surface"
            >
              <Sparkles size={14} className="text-highlight" />
              Free Savings Analysis
            </Link>
          </div>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Connect in under 5 minutes · Agentless by default · SOC 2 compliant
          </p>
        </motion.div>

        <G2Featured />

        <div className="mt-20">
          <p className="mb-5 text-center text-[10px] uppercase tracking-widest text-muted-foreground">
            Plus 26 more G2 badges across Spring 2026 grids & indexes
          </p>
          <BadgeMarquee />
        </div>
      </div>
    </section>
  );
}
